import { useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { Moon, Shield, Zap, ArrowRight, Check } from "lucide-react";
import SubscribeModal from "../components/SubscribeModal";
import { Link } from "react-router-dom";
import { adrust, ADRUST_PATH } from "../content/adrust";

const TIER_KEYS = ["basic", "pro", "premium"];

export default function Nachtrust() {
  const { t, lang } = useLanguage();
  const tiers = t("nachtrust.tiers");
  const [modalTier, setModalTier] = useState(null);

  return (
    <section
      id="nachtrust"
      data-testid="nachtrust-section"
      className="relative py-24 md:py-36 overflow-hidden"
      style={{
        backgroundColor: "#232750",
        color: "#F2F4F8",
      }}
    >
      {/* Background image */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1689563829225-2acfdcc2b543?auto=format&fit=crop&w=1600&q=60&fm=webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.45,
        }}
      />
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(180deg, rgba(35,39,80,0.85) 0%, rgba(35,39,80,0.7) 40%, rgba(35,39,80,0.95) 100%)",
        }}
      />
      <div className="noise-overlay-dark -z-10" />

      {/* Stars */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        {Array.from({ length: 30 }).map((_, i) => (
          <span
            key={i}
            className="star absolute bg-white rounded-full"
            style={{
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
            }}
          />
        ))}
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-20">
          <div className="col-span-12 md:col-span-8">
            <div className="flex items-center gap-3 mb-4">
              <Moon size={16} className="text-green" />
              <span className="eyebrow text-green" data-testid="nachtrust-overline">
                {t("nachtrust.overline")} · 05
              </span>
            </div>
            <h2 className="heading-display text-5xl md:text-7xl lg:text-8xl text-white" data-testid="nachtrust-title">
              {t("nachtrust.title")}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 self-end">
            <p className="text-base md:text-lg text-[#B5C0D4] leading-relaxed">
              {t("nachtrust.sub")}
            </p>
          </div>
        </div>

        {/* Pricing tiers */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {tiers.map((tier, i) => (
            <article
              key={tier.name}
              data-testid={`nachtrust-tier-${tier.name.toLowerCase()}`}
              className={`glass-dark relative p-8 md:p-10 transition-all duration-500 hover:-translate-y-1 flex flex-col ${
                tier.highlight ? "ring-1 ring-green/60 bg-white/[0.06]" : ""
              }`}
            >
              {tier.badge && (
                <span
                  data-testid={`nachtrust-badge-${tier.name.toLowerCase()}`}
                  className="absolute top-4 right-4 text-[10px] tracking-[0.2em] uppercase bg-green text-navy px-3 py-1 rounded-full"
                >
                  {tier.badge}
                </span>
              )}

              <div className="flex items-center gap-2 mb-6">
                {i === 0 && <Moon size={18} className="text-green" />}
                {i === 1 && <Shield size={18} className="text-green" />}
                {i === 2 && <Zap size={18} className="text-green" />}
                <span className="eyebrow text-green">{tier.name}</span>
              </div>

              <div className="flex items-baseline gap-1 mb-3">
                <span className="heading-display text-6xl md:text-7xl text-white">
                  {tier.price}
                </span>
                <span className="text-white/70 text-sm">{tier.cycle}</span>
              </div>
              <p className="text-[#B5C0D4] text-sm mb-8 leading-relaxed">
                {tier.desc}
              </p>

              <ul className="space-y-3 mb-10 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-[#D6DDEA]">
                    <Check size={14} className="mt-1 text-green flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() =>
                  setModalTier({
                    key: TIER_KEYS[i],
                    name: tier.name,
                    price: tier.price,
                  })
                }
                data-testid={`nachtrust-cta-${tier.name.toLowerCase()}`}
                className={`btn-editorial ${
                  tier.highlight ? "btn-editorial-light" : "btn-editorial-outline"
                } justify-center`}
                style={
                  tier.highlight
                    ? {}
                    : { borderColor: "#E2E8F0", color: "#F2F4F8" }
                }
              >
                {tier.cta} <ArrowRight size={16} />
              </button>
            </article>
          ))}
        </div>

        <p className="text-center text-white/70 mt-10 text-sm">
          {t("nachtrust.guarantee")}
        </p>

        {lang === "nl" && (
          <div data-testid="adrust-bar" className="mt-12 border border-white/15 rounded-2xl md:rounded-full px-6 md:px-8 py-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-6 text-sm">
            <span className="text-[#F7F5F0] whitespace-nowrap"><strong className="font-semibold">{adrust.vermelding.naam}</strong> · {adrust.vermelding.prijs}</span>
            <span className="text-white/70 flex-1">{adrust.vermelding.tekst}</span>
            <Link to={ADRUST_PATH} data-testid="adrust-bar-link" className="text-green hover:text-[#F7F5F0] transition-colors whitespace-nowrap">{adrust.vermelding.link}</Link>
          </div>
        )}
      </div>

      <SubscribeModal
        tier={modalTier}
        open={!!modalTier}
        onClose={() => setModalTier(null)}
      />
    </section>
  );
}
